import { z } from 'zod';

export const publicConfigSchema = z.object({
  glitchtipDsn: z.string().nullable(),
  release: z.string(),
  environment: z.enum(['development', 'test', 'production']),
});

export type PublicConfig = z.infer<typeof publicConfigSchema>;

/**
 * How the event identifies itself to a volunteer.
 *
 * All three land on the briefing card's contact block, which is the one screen
 * a coordinator never sees — so unlike the autofill settings, editing these
 * changes what people already holding a briefing read, immediately and without
 * a version bump.
 *
 * `opsPhone` may be empty, because a coordinator setting up an event often does
 * not have the number yet. The briefing leaves out every line that needs a value
 * it does not have — the call button, the WhatsApp group, the deadline — so an
 * empty field hides its line rather than rendering a link that does nothing.
 *
 * The WhatsApp link is an invitation to a group, so only an invite URL is
 * accepted: anything else on that button would send a marshal somewhere else.
 *
 * The briefing fields are optional, not defaulted: a client that does not know
 * them yet (a tab open across the deploy) saves the name without blanking them.
 */
export const eventIdentitySchema = z.object({
  name: z.string().trim().min(1).max(200),
  opsPhone: z.string().trim().max(40).default(''),
  opsName: z.string().trim().max(80).optional(),
  mailbox: z.union([z.literal(''), z.email().max(200)]).default(''),
  whatsappName: z.string().trim().max(80).optional(),
  whatsappUrl: z
    .union([z.literal(''), z.string().trim().regex(/^https:\/\/chat\.whatsapp\.com\/[A-Za-z0-9]+(\?\S*)?$/)])
    .optional(),
  /** The organisers' emergency number, as printed in the race guide and on every bib. */
  emergencyPhone: z.string().trim().max(40).optional(),
  answerDeadline: z.iso.date().nullable().optional(),
});

export type EventIdentity = z.infer<typeof eventIdentitySchema>;
